import { Geld, useGeld } from '../lib/geld';
import { feestToeslag } from '../lib/feest';
import { useMemo } from 'react';
import { format } from 'date-fns';
import { nl } from 'date-fns/locale';
import { MapPin } from 'lucide-react';
import { cn, formatHours, formatMoney, formatTime, rateTypeLabel } from '../lib/utils';
import { VerlengdTag } from './Verlengd';
import type { Holiday, ShiftWithCalc } from '../types';
import { afwijkingInfo } from './AfwijkingPanel';
import { shiftKind, KIND_LABEL, KIND_STYLE } from '../lib/shift-kind';
import { Disclosure, ICON } from './ui';

interface Props {
  cursor: Date;
  shifts: ShiftWithCalc[];
  holiday: Holiday | null;
  onShiftClick: (shiftId: number) => void;
  onEmptySlotClick: (date: string, startTime: string) => void;
}

const HOUR_HEIGHT = 48;
const HOURS = Array.from({ length: 24 }, (_, i) => i);

export function CalendarDayView({ cursor, shifts, holiday, onShiftClick, onEmptySlotClick }: Props) {
  const { geld, bedrag, label } = useGeld();
  const dateStr = format(cursor, 'yyyy-MM-dd');
  const today = format(new Date(), 'yyyy-MM-dd');
  const isToday = dateStr === today;

  const dayShifts = useMemo(() => shifts.filter((s) => s.shift_date === dateStr), [shifts, dateStr]);

  const totalHours = dayShifts.reduce((sum, s) => sum + s.total_hours, 0);
  const totalGross = dayShifts.reduce((sum, s) => sum + s.total_gross, 0);
  const totalReis = dayShifts.reduce((sum, s) => sum + (s.reiskosten ?? 0), 0);

  function handleSlotClick(e: React.MouseEvent) {
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const y = e.clientY - rect.top + (e.currentTarget as HTMLElement).scrollTop;
    const hour = Math.max(0, Math.min(23, Math.floor(y / HOUR_HEIGHT)));
    onEmptySlotClick(dateStr, `${String(hour).padStart(2, '0')}:00`);
  }

  return (
    <div className="grid gap-3 sm:gap-4 lg:grid-cols-[minmax(0,1fr)_320px] [&>*]:min-w-0">
      <div className="card-flush enter">
        <div className="card-head border-b border-surface-line">
          <h2 className="min-w-0 truncate first-letter:uppercase">
            {format(cursor, 'EEEE d MMMM', { locale: nl })}
            {isToday && <span className="ml-2 font-normal text-slate-400">vandaag</span>}
          </h2>
          {holiday && <span className="badge-feest shrink-0">{holiday.name}, {feestToeslag(dateStr).lang}</span>}
        </div>

        <div className="relative max-h-[calc(100vh-320px)] min-h-[320px] overflow-y-auto" onClick={handleSlotClick}>
          <div className="relative" style={{ height: HOUR_HEIGHT * 24 }}>
            {HOURS.map((h) => (
              <div
                key={h}
                className="absolute left-0 right-0 border-t border-surface-line/40"
                style={{ top: h * HOUR_HEIGHT, height: HOUR_HEIGHT }}
              >
                <div className="t-num w-14 pl-3 pt-1 text-xs text-slate-500">{String(h).padStart(2, '0')}:00</div>
              </div>
            ))}

            {dayShifts.map((s) => (
              <DayShiftBlock
                key={s.id}
                shift={s}
                onClick={(e) => {
                  e.stopPropagation();
                  onShiftClick(s.id);
                }}
              />
            ))}
          </div>

          {dayShifts.length === 0 && (
            <div className="pointer-events-none absolute inset-x-0 top-24 flex justify-center">
              <span className="rounded-lg bg-surface px-3 py-1.5 text-sm text-slate-400">Vrij. Klik op een uur om een dienst toe te voegen.</span>
            </div>
          )}
        </div>
      </div>

      <div className="space-y-3 sm:space-y-4">
        <section className="card enter">
          <h2 className="mb-3">Deze dag</h2>
          <dl className="grid grid-cols-3 gap-3">
            <Stat label="Uren" value={formatHours(totalHours)} />
            <Stat label={label} value={geld(totalGross, dateStr)} />
            <Stat label="Reiskosten" value={formatMoney(totalReis)} />
          </dl>
          <div className="mt-3 flex items-baseline justify-between border-t border-surface-line pt-3 text-sm">
            <span className="text-slate-400">Totaal</span>
            <span className="t-num font-semibold text-money">{formatMoney((bedrag(totalGross, dateStr) ?? 0) + totalReis)}</span>
          </div>
        </section>

        {dayShifts.length > 0 && (
          <section className="card-flush enter">
            <div className="list">
              {dayShifts.map((s) => (
                <DayShiftDetail key={s.id} shift={s} today={today} onOpen={() => onShiftClick(s.id)} />
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <dt className="t-label">{label}</dt>
      <dd className="t-num mt-1 truncate text-sm font-semibold text-slate-100">{value}</dd>
    </div>
  );
}

/** Eén dienst in de zijkolom: tijd, soort, status; de berekening pas na openklappen. */
function DayShiftDetail({ shift: s, today, onOpen }: { shift: ShiftWithCalc; today: string; onOpen: () => void }) {
  const kind = shiftKind(s);
  const afw = afwijkingInfo(s.deviation_reason);
  const leave = s.leave_type && s.leave_type !== 'work';
  const AfwIcon = afw?.icon;
  return (
    <div>
      <button type="button" onClick={onOpen} className="row-link">
        <span className={cn('h-2 w-2 shrink-0 rounded-full', KIND_STYLE[kind].dot)} aria-hidden />
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2">
            <span className="t-num text-sm font-semibold text-slate-100">
              {formatTime(s.start_time)}–{formatTime(s.end_time)}
            </span>
            <VerlengdTag shift={s} />
            <span className="text-xs text-slate-400">{KIND_LABEL[kind]}</span>
          </div>
          <div className="flex min-w-0 items-center gap-1 text-xs text-slate-400">
            <MapPin className={cn(ICON.sm, 'shrink-0 text-slate-500')} aria-hidden />
            <span className="truncate">{s.object?.naam ?? s.object_naam_snapshot ?? 'Geen object'}</span>
          </div>
        </div>
        <div className="shrink-0 text-right">
          <div className="t-num text-sm text-slate-100"><Geld bruto={s.total_gross} date={s.shift_date} /></div>
          {afw && AfwIcon ? (
            <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-200">
              <AfwIcon className={ICON.sm} aria-hidden /> {afw.label}
            </span>
          ) : leave ? (
            <span className="status-warn">{s.leave_type === 'ziekte' ? 'Ziek' : 'Verlof'}</span>
          ) : (
            <span className={s.shift_date <= today ? 'status-ok' : 'status-muted'}>{s.shift_date <= today ? 'Gewerkt' : 'Gepland'}</span>
          )}
        </div>
      </button>
      {s.segments.length > 0 && (
        <Disclosure title="Berekening" meta={`${s.segments.length} ${s.segments.length === 1 ? 'tarief' : 'tariefdelen'}`}>
          <table className="table">
            <tbody>
              {s.segments.map((seg) => (
                <tr key={seg.id}>
                  <td className="!px-0 text-slate-300">{rateTypeLabel(seg.rate_type)}</td>
                  <td className="r !px-0 text-slate-500">
                    {formatHours(seg.hours)} × {seg.multiplier.toFixed(2).replace('.', ',')}
                  </td>
                  <td className="r !px-0 text-slate-100">{formatMoney(seg.total_amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {s.notes && <p className="mt-3 border-l-2 border-surface-line pl-3 text-sm text-slate-400">{s.notes}</p>}
        </Disclosure>
      )}
    </div>
  );
}

function DayShiftBlock({ shift, onClick }: { shift: ShiftWithCalc; onClick: (e: React.MouseEvent) => void }) {
  const [sh, sm] = shift.start_time.split(':').map(Number);
  const [eh, em] = shift.end_time.split(':').map(Number);
  const startPos = (sh + sm / 60) * HOUR_HEIGHT;
  const endHour = shift.crosses_midnight ? eh + 24 : eh;
  const endPos = Math.min(24 * HOUR_HEIGHT, (endHour + em / 60) * HOUR_HEIGHT);
  const height = Math.max(24, endPos - startPos);
  const kind = shiftKind(shift);
  const st = KIND_STYLE[kind];

  return (
    <div
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          e.currentTarget.click();
        }
      }}
      role="button"
      tabIndex={0}
      className={cn(
        'absolute left-14 right-3 cursor-pointer overflow-hidden rounded-lg border-l-2 px-3 py-2 transition-[filter] duration-fast ease-calm hover:brightness-125',
        st.bar,
        st.bg
      )}
      style={{ top: startPos, height }}
    >
      <div className="flex items-baseline gap-2">
        <span className="t-num text-sm font-semibold text-slate-50">
          {formatTime(shift.start_time)}–{formatTime(shift.end_time)}
        </span>
        <VerlengdTag shift={shift} />
        <span className={cn('text-xs', st.text)}>{KIND_LABEL[kind]}</span>
      </div>
      {height > 40 && <div className="truncate text-xs text-slate-300">{shift.object?.naam ?? shift.object_naam_snapshot ?? '–'}</div>}
      {height > 70 && shift.total_gross > 0 && (
        <div className="t-num mt-1 text-xs text-slate-400">
          {formatHours(shift.total_hours)}, <Geld bruto={shift.total_gross} date={shift.shift_date} />
        </div>
      )}
    </div>
  );
}
